import { Routes } from '@angular/router';
import {
  canActivate,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import { LoginPage } from './pages/auth/login/login.page';
import { hasCustomClaimRedirect, isEthikratMember } from './util/AuthGuard';
import { HomePage } from './pages/dashboard/home/home.page';
import { OnboardingPage } from './pages/auth/onboarding/onboarding.page';

const redirectLoggedInToDashboard = () => redirectLoggedInTo(['/dashboard']);

const redirectUnauthorizedToLogin = () =>
  redirectUnauthorizedTo(['/auth/landing']);

const isOnboardedUser = () => hasCustomClaimRedirect('experience_user_details', '/auth/onboarding');
const isVerifiedUser = () => hasCustomClaimRedirect('verified', '/dashboard');
const isAdmin = () => hasCustomClaimRedirect('admin', '/not-authorized');

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/splash/splash.page').then(m => m.SplashPage),
    pathMatch: 'full'
  },

  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard.page').then(m => m.DashboardPage),
    children: [
      {
        path: 'post/:id',
        loadComponent: () => import('./pages/shared/post-detail/post-detail.page').then(m => m.PostDetailPage)
      },
      {
        path: 'hub',
        loadComponent: () => import('./pages/dashboard/hub/hub.page').then(m => m.HubPage)
      },
      {
        path: 'hub/:id',
        loadComponent: () => import('./pages/dashboard/hub/regional-hub-detail/regional-hub-detail.page').then(m => m.RegionalHubDetailPage)
      },
      {
        path: 'business-hub/:id',
        loadComponent: () => import('./pages/dashboard/hub/business-hubs/business-hub-detail/business-hub-detail.page').then(m => m.BusinessHubDetailPage)
      },
      {
        path: '',
        title: 'Profil',
        component: HomePage,
      },
      {
        path: 'search',
        loadComponent: () => import('./pages/dashboard/search/search.page').then(m => m.SearchPage)
      },

      {
        path: 'ethikrat/applications',
        title: 'Meine Ethikrat Bewerbungen',
        loadComponent: () => import('./pages/dashboard/ethikrat/applications/applications.page').then(m => m.EthikratApplicationsPage)
      },
      {
        path: 'ethikrat',
        title: 'Ethikrat Dashboard',
        loadComponent: () => import('./pages/dashboard/ethikrat/reviews/reviews.page').then(m => m.EthikratReviewsPage),
        ...canActivate(isEthikratMember)
      },
      {
        path: 'ethikrat/product/:id',
        title: 'Produkt bewerten',
        loadComponent: () => import('./pages/dashboard/ethikrat/product-detail/product-detail.page').then(m => m.ProductDetailPage),
        ...canActivate(isEthikratMember)
      },
      {
        path: 'settings',
        title: 'Einstellungen',
        loadComponent: () => import('./pages/dashboard/settings/settings.page').then(m => m.SettingsPage)
      },
      {
        path: 'regio-shop',
        loadComponent: () => import('./pages/dashboard/regio-shop/regio-shop.page').then(m => m.RegioShopPage)
      }, {
        path: 'profile/:userId',
        title: 'Profil',
        loadComponent: () => import('./pages/dashboard/home/home.page').then(m => m.HomePage)

      },
      {
        path: 'gemeindeblatt/:id',
        loadComponent: () => import('./pages/dashboard/gemeindeblatt/gemeindeblatt-viewer/gemeindeblatt-viewer.page').then(m => m.GemeindeblattViewerPage)
      },
      {
        path: 'gemeindeblatt',
        loadComponent: () => import('./pages/dashboard/gemeindeblatt/gemeindeblatt-list/gemeindeblatt-list.page').then(m => m.GemeindeblattListPage)
      },


      {
        path: 'regio-shop/checkout',
        loadComponent: () => import('./pages/dashboard/regio-shop/checkout/checkout.page').then(m => m.CheckoutPage)
      },
      {
        path: 'regio-shop/cart',
        loadComponent: () => import('./pages/dashboard/regio-shop/cart/cart.page').then(m => m.CartPage)
      },
      {
        path: 'orders/:id',
        loadComponent: () => import('./pages/dashboard/regio-shop/order-detail/order-detail.page').then(m => m.OrderDetailPage)
      },
      // ========== TICKETS ROUTES ==========
      {
        path: 'my-tickets',
        loadComponent: () => import('./pages/dashboard/my-tickets/my-tickets.page').then(m => m.MyTicketsPage)
      },
      {
        path: 'my-tickets/:id',
        loadComponent: () => import('./pages/dashboard/my-tickets/ticket-view/ticket-view.page').then(m => m.TicketViewPage)
      },
      {
        path: 'regio-shop/order-success',
        loadComponent: () => import('./pages/dashboard/regio-shop/success/success.page').then(m => m.SuccessPage)
      },
      {
        path: 'regio-shop/:category',
        loadComponent: () => import('./pages/dashboard/regio-shop/category/category.page').then(m => m.CategoryPage)
      }, {
        path: 'regio-shop/product/:id',
        loadComponent: () => import('./pages/dashboard/regio-shop/product-detail/product-detail.page').then(m => m.ProductDetailPage)
      },
      {
        path: 'energy',
        title: 'Energie-Management',
        loadComponent: () => import('./pages/energy/energy-dashboard.page').then(m => m.EnergyDashboardPage)
      },

    ],
    ...canActivate(redirectUnauthorizedToLogin),
    ...canActivate(isOnboardedUser),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'landing',
        loadComponent: () => import('./pages/auth/landing/landing.page').then(m => m.LandingPage)
      },
      {
        path: 'login',
        component: LoginPage,
      },
      {
        path: 'register',
        redirectTo: 'login',
        pathMatch: 'full'
      },
      {
        path: 'onboarding',
        component: OnboardingPage,
        ...canActivate(redirectUnauthorizedToLogin),
      }
    ],
  },
  {
    path: 'landing',
    loadComponent: () => import('./pages/landing/landing.page').then(m => m.LandingPage)
  },
  {
    path: 'imprint',
    loadComponent: () => import('./pages/landing/imprint/imprint.page').then(m => m.ImprintPage)
  },
  {
    path: 'agbs',
    loadComponent: () => import('./pages/landing/agbs/agbs.component').then(m => m.AgbsComponent)
  },
  {
    path: 'privacy',
    loadComponent: () => import('./pages/landing/privacy/privacy.component').then(m => m.PrivacyComponent)
  },

];